import { ButtonWithDropdownMenu, type DropdownMenu } from '@strata/react-dropdown-menu';
import { SuccessToast } from '@strata/react-toast-message';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type ComponentProps } from 'react';

import { CaseAssignmentDialog } from './dialogs/CaseAssignmentDialog';
import { LogPriorAuthCommunicationDialog } from './dialogs/LogPriorAuthCommunicationDialog';
import { PauseIntakeCaseDialog } from './dialogs/PauseIntakeCaseDialog';

import { AttachDocumentsDialog } from 'components/intakeCases/dialogs/AttachDocumentsDialog';
import { CancelIntakeCaseDialog } from 'components/intakeCases/dialogs/CancelIntakeCaseDialog';
import { LogCommunicationDialog } from 'components/intakeCases/dialogs/LogCommunicationDialog';
import { ReopenIntakeCaseDialog } from 'components/intakeCases/dialogs/ReopenIntakeCaseDialog';
import { SendFaxDialog } from 'components/intakeCases/dialogs/SendFaxDialog';
import {
    ActionMenuDialogs,
    type ActionMenuDialogType,
    PendingActionTypes,
    Permissions,
} from 'services/constants';
import {
    canCancelIntakeCase,
    canSendPrescriberFax,
    hasIntakeCaseBeenArchived,
} from 'services/helpers';
import { useAppSelector } from 'store';
import { type IntakeCase, usePostUnpauseIntakeCaseMutation } from 'store/api/intakeCaseApi';

interface OpenActionMenuDialogAction {
    type: typeof PendingActionTypes.OPEN_ACTION_MENU_DIALOG;
    dialog: ActionMenuDialogType;
}

interface IntakeCaseDetailsActionsMenuProps {
    intakeCase: Pick<
        IntakeCase,
        | 'intakeCaseId'
        | 'intakeStatus'
        | 'memberId'
        | 'memberFirstName'
        | 'memberLastName'
        | 'isBrand'
        | 'prescriberPracticeName'
        | 'prescriberFirstName'
        | 'prescriberLastName'
        | 'prescriberFaxNumber'
        | 'outreaches'
        | 'assignedUsername'
        | 'pausedAt'
        | 'prescriberVerified'
        | 'monitoringCase'
    >;
    locked?: boolean;
    openDialog: ActionMenuDialogType | null;
    onOpenDialog: (dialog: ActionMenuDialogType | null) => void;
    onCloseDialog: () => void;
    onActionWithUnsavedChangesGuard?: (
        action: OpenActionMenuDialogAction,
        immediateAction: () => void,
    ) => void;
    useGuardForLogPriorAuth?: boolean;
    isValidIntakeCase: boolean;
}

export const IntakeCaseDetailsActionsMenu = ({
    intakeCase,
    locked = false,
    openDialog,
    onOpenDialog,
    onCloseDialog,
    onActionWithUnsavedChangesGuard = undefined,
    useGuardForLogPriorAuth = false,
    isValidIntakeCase,
}: IntakeCaseDetailsActionsMenuProps) => {
    const { showIntakeCasePauseOption } = useFlags<{
        showIntakeCasePauseOption: boolean;
    }>();
    const permissions = useAppSelector((state) => state.auth.permissions);
    const hasWriteIntakeCaseAdmin = permissions.includes(Permissions.WRITE_INTAKE_CASE_ADMIN);
    const isArchivedIntakeCase = hasIntakeCaseBeenArchived(intakeCase.intakeStatus);
    const [postUnpauseIntakeCase] = usePostUnpauseIntakeCaseMutation();
    const hasFaxNumber = !!intakeCase.prescriberFaxNumber;

    const requestDialogOpen = (dialog: ActionMenuDialogType, useGuard = true) => {
        const immediateAction = () => onOpenDialog(dialog);

        if (onActionWithUnsavedChangesGuard && useGuard) {
            onActionWithUnsavedChangesGuard(
                {
                    type: PendingActionTypes.OPEN_ACTION_MENU_DIALOG,
                    dialog,
                },
                immediateAction,
            );
            return;
        }

        immediateAction();
    };

    const menuItems: ComponentProps<typeof DropdownMenu>['menuItemArray'] = hasFaxNumber
        ? [
              {
                  text: 'Send Fax',
                  disabled: !canSendPrescriberFax(intakeCase, permissions) || locked,
                  onItemClick: () => requestDialogOpen(ActionMenuDialogs.SEND_FAX),
              },
          ]
        : [];
    if (!isArchivedIntakeCase) {
        menuItems.push({
            text: 'Attach Documents',
            disabled: !permissions.includes(Permissions.WRITE_INTAKE_CASE) || locked,
            onItemClick: () => requestDialogOpen(ActionMenuDialogs.ATTACH_DOCUMENTS),
        });
        menuItems.push({
            text: 'Cancel Intake Case',
            disabled: !canCancelIntakeCase(intakeCase, permissions) || locked,
            onItemClick: () => requestDialogOpen(ActionMenuDialogs.CANCEL_INTAKE_CASE),
        });
        if (showIntakeCasePauseOption) {
            if (intakeCase.pausedAt) {
                menuItems.push({
                    text: 'Unsnooze Intake Case',
                    disabled: !hasWriteIntakeCaseAdmin,
                    onItemClick: () => {
                        void (async () => {
                            await postUnpauseIntakeCase({
                                intakeCaseId: intakeCase.intakeCaseId,
                            }).unwrap();
                            SuccessToast({ message: 'Intake case resumed successfully.' });
                        })();
                    },
                });
            } else {
                menuItems.push({
                    text: 'Snooze Intake Case',
                    disabled: !hasWriteIntakeCaseAdmin,
                    onItemClick: () => requestDialogOpen(ActionMenuDialogs.PAUSE_INTAKE_CASE),
                });
            }
        }
    } else {
        menuItems.push({
            text: 'Reopen Intake Case',
            disabled: locked,
            onItemClick: () => requestDialogOpen(ActionMenuDialogs.REOPEN_INTAKE_CASE),
        });
    }
    menuItems.push({
        text: 'Log Prior Auth Activity',
        disabled: locked,
        onItemClick: () =>
            requestDialogOpen(ActionMenuDialogs.LOG_PRIOR_AUTH, useGuardForLogPriorAuth),
    });
    menuItems.push({
        text: 'Select/Change Assignment',
        disabled: !permissions.includes(Permissions.WRITE_INTAKE_CASE),
        onItemClick: () => requestDialogOpen(ActionMenuDialogs.CASE_ASSIGNMENT),
    });
    return (
        <>
            <LogCommunicationDialog
                isOpen={false}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <LogPriorAuthCommunicationDialog
                isOpen={openDialog === ActionMenuDialogs.LOG_PRIOR_AUTH}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
                isValidIntakeCase={isValidIntakeCase}
            />
            <AttachDocumentsDialog
                isOpen={openDialog === ActionMenuDialogs.ATTACH_DOCUMENTS}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <ReopenIntakeCaseDialog
                isOpen={openDialog === ActionMenuDialogs.REOPEN_INTAKE_CASE}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <SendFaxDialog
                isOpen={openDialog === ActionMenuDialogs.SEND_FAX}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <CancelIntakeCaseDialog
                isOpen={openDialog === ActionMenuDialogs.CANCEL_INTAKE_CASE}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <CaseAssignmentDialog
                isOpen={openDialog === ActionMenuDialogs.CASE_ASSIGNMENT}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
            <ButtonWithDropdownMenu
                menuItems={menuItems}
                label="Actions"
                intent={ButtonWithDropdownMenu.Intent.PRIMARY}
            />
            <PauseIntakeCaseDialog
                isOpen={openDialog === ActionMenuDialogs.PAUSE_INTAKE_CASE}
                onClose={onCloseDialog}
                intakeCase={intakeCase}
            />
        </>
    );
};
